import _ from 'lodash';
import React, { useCallback, useRef, useState } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';

interface IApiFetchCachingContext {
  get<R>(url: string, opts?: { skipCache?: boolean }): Promise<R>;
  post<R>(url: string, body: any, opts?: { skipCache?: boolean }): Promise<R>;
  clearCache(pattern?: string): void;
  cacheVersion: number;
}

// default value performs no caching
const DEFAULT_API_FETCH_CONTEXT_VALUE: IApiFetchCachingContext = {
  get<R>(url: string): Promise<R> {
    return apiFetch(null, url);
  },
  post<R>(url: string, body: any): Promise<R> {
    return apiFetch(null, url, { method: 'POST', body });
  },
  clearCache: _.noop,
  cacheVersion: 0,
};

/**
 * A react context for caching queries -- both GET and POST -- to the internal server by wrapping apiFetch with a cache that is keyed by url and stringified body.
 * Pass { skipCache: true } to force clearing the cached value and fetching a new value for a particular url
 *
 * Does not yet support apiFetch opts.
 */
export const ApiFetchCachingContext = React.createContext<IApiFetchCachingContext>(
  DEFAULT_API_FETCH_CONTEXT_VALUE,
);

export const ApiFetchCachingContextProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const cache = useRef<Map<string, Promise<any>>>(new Map()).current;
  const [cacheVersion, setCacheVersion] = useState(0);
  const get = useCallback(function <R>(url: string, opts?: { skipCache?: boolean }): Promise<R> {
    if (!cache.has(url) || opts?.skipCache) {
      cache.set(url, apiFetch(null, url));
    }
    return cache.get(url)!;
  }, []);
  const post = useCallback(function <R>(
    url: string,
    body: any,
    opts?: { skipCache?: boolean },
  ): Promise<R> {
    const key = JSON.stringify([url, body]);
    if (!cache.has(key) || opts?.skipCache) {
      cache.set(key, apiFetch(null, url, { method: 'POST', body }));
    }
    return cache.get(key)!;
  },
  []);
  const clearCache = useCallback(() => {
    cache.clear();
    setCacheVersion(v => v + 1);
  }, []);
  return (
    <ApiFetchCachingContext.Provider value={{ get, post, clearCache, cacheVersion }}>
      {children}
    </ApiFetchCachingContext.Provider>
  );
};
